import { useAuthStore } from "@/store/authStore";

/**
 * Why a session ended, when the login page owes the reader an explanation.
 * Each has its sentence on the login page (`SIGNED_OUT_MESSAGES`).
 *
 * - `idle`: no keyboard, mouse, touch or scroll input for the idle timeout.
 * - `session_expired`: the server refused to extend a session past its
 *   absolute lifetime.
 * - `mfa_locked`: too many wrong second-factor codes ended the sign-in.
 * - `mfa_sign_in_complete`: this pending sign-in was already completed.
 */
export type SignOutReason =
  | "idle"
  | "session_expired"
  | "mfa_locked"
  | "mfa_sign_in_complete";

/** The login page's query parameter carrying the reason. */
export const SIGNED_OUT_PARAM = "signed_out";

/**
 * Where a reason is left for the other tabs of the origin. Every tab ends when
 * one signs out, but only the tab that saw the cause knows it; the rest read it
 * here so the page the reader is actually looking at explains itself too.
 */
const REASON_KEY = "everythingcloud-sign-out-reason";

/** How long a recorded reason explains another tab's sign-out. The `storage`
 *  event arrives within milliseconds; anything older is a different sign-out. */
const REASON_FRESH_MS = 10_000;

interface RecordedReason {
  reason: SignOutReason;
  at: number;
  params?: Record<string, string>;
}

function recordReason(entry: RecordedReason): void {
  try {
    window.localStorage.setItem(REASON_KEY, JSON.stringify(entry));
  } catch {
    // Storage full or blocked: this tab still explains itself.
  }
}

/** The reason another tab recorded for a sign-out that just happened, if any. */
export function recentSignOutReason():
  | { reason: SignOutReason; params?: Record<string, string> }
  | undefined {
  try {
    const raw = window.localStorage.getItem(REASON_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<RecordedReason>;
    if (
      typeof parsed.reason !== "string" ||
      typeof parsed.at !== "number" ||
      Date.now() - parsed.at > REASON_FRESH_MS
    ) {
      return undefined;
    }
    return { reason: parsed.reason, params: parsed.params };
  } catch {
    return undefined;
  }
}

/** The login address, carrying the reason when there is one. */
export function loginPath(
  reason?: SignOutReason,
  params: Record<string, string> = {},
): string {
  if (!reason) return "/login";
  const query = new URLSearchParams({ [SIGNED_OUT_PARAM]: reason, ...params });
  return `/login?${query.toString()}`;
}

/**
 * End this tab's session and leave for the login page.
 *
 * Several callers reach this: the axios interceptor when a 401 cannot be
 * refreshed, the cross-tab listener when another tab signs out, the poll when
 * the session was revoked from another device, the idle timer, and the MFA
 * challenge when the sign-in it was completing has ended. All of them were
 * doing `logout(); window.location.href = "/login"` by hand, and only one
 * guarded against running twice — so a burst of concurrent 401s could fire
 * several logouts and several navigations.
 *
 * The guard is the store's own state rather than a module flag. `logout()`
 * clears `isAuthenticated` synchronously, so the second caller in a burst finds
 * nothing left to end and returns. That also means it resets itself on the next
 * sign-in, which a module flag would not — it would silently disable sign-out
 * for the rest of the page's life, and would leak between tests.
 *
 * A `reason` is carried to the login page, which says what happened; it is
 * recorded for the other tabs before the sign-out they react to is written.
 */
export function endSession(
  reason?: SignOutReason,
  params?: Record<string, string>,
): void {
  const { isAuthenticated, logout } = useAuthStore.getState();
  if (!isAuthenticated) return;
  if (reason) recordReason({ reason, at: Date.now(), params });
  logout();
  window.location.href = loginPath(reason, params);
}
